import type { DocNode } from '@atlaskit/adf-schema/doc';

import { type RenderState, type Handler, type ForgeDoc } from './forge';

export interface BackendRuntimePayload {
	// v: 2; @TODO revisit whether we need this version. Will likely need GQL gateway upgrade.
	effects: BackendEffect[];
}

export type ExtensionData = {
	[k: string]: any;
	macro?: {
		isConfiguring?: boolean;
		isInserting?: boolean;
		layout?: 'bodied';
	};
};

export type ExtensionPayload = { [k: string]: any };

export type ExtensionViewData = {
	[k: string]: any;
	macro?: {
		body?: DocNode;
	};
};

export interface CoreData {
	/**
	 * Optional due to products with workspace install
	 */
	appVersion?: string;
	cloudId?: string;
	environmentId?: string;
	environmentType?: string;
	// localId uniquely identifies the instance of the extension
	localId: string;
	workspaceId?: string;
}

export interface CoreDataInner extends CoreData {
	moduleKey: string;
	siteUrl: string;
}

export interface EventEffect {
	args: any[];
	coreData: CoreData;
	extensionData: ExtensionData;
	handler: Handler;
	state: RenderState;
	type: 'event';
}

export interface ActionEffect {
	componentKey: string;
	coreData: CoreData;
	extensionData: ExtensionData;
	hookIndex: number;
	payload?: any;
	state: RenderState;
	type: 'action';
}

export interface RenderEffect {
	coreData: CoreData;
	extensionData: ExtensionData;
	state: RenderState;
	type: 'render';
}

export interface ResultEffect {
	forgeDoc: ForgeDoc;
	state: RenderState; // this gets encrypted and is a string on the client
	type: 'result';
}

export type BackendEffect = RenderEffect | ActionEffect | EventEffect;
export type ClientEffect = ResultEffect;
export type Effect = BackendEffect | ClientEffect;

export const isEventEffect = (effect: Effect): effect is EventEffect => {
	return effect.type === 'event';
};

export const isActionEffect = (effect: Effect): effect is ActionEffect => {
	return effect.type === 'action';
};

export const isRenderEffect = (effect: Effect): effect is RenderEffect => {
	return effect.type === 'render';
};

export const isResultEffect = (effect: Effect): effect is ResultEffect => {
	return effect.type === 'result';
};

export function isBackendEffect(effect: Effect): effect is BackendEffect {
	return isActionEffect(effect) || isEventEffect(effect) || isRenderEffect(effect);
}
